import { memo } from "react";
import DeliveryManagementPlugin from "@delivery-management/DeliveyMangement/DeliveryManagementPlugin.tsx";
import { computed, effectOn, selector, signal, useAsyncEffect } from "@/react/core/reactive.ts";
import { users0 } from "@/data/UserSignal.ts";
import { UserRole } from "@/data/User.ts";
import { OnlineOrder } from "@/data/OnlineOrder.ts";
import { kitchenOrderStatus, pendingOrderStatus } from "@/react/PendingOrder/PendingOrderLogic.tsx";
import GoogleMapsProvider from "@/react/core/GoogleMapsProvider.tsx";
import type { Order } from "@/data/Order.ts";
import _ from "lodash";
import { DeliveryStatus, OrderStatus, type OrderStrip } from "@/pos/OrderType.ts";
import { dataLock } from "@/data/DataUtils.ts";
import { makeDeliverySettingsAvailable } from "@/data/DeliverySettingsHub.ts";
import dayjs from "dayjs";
import { makeOnlineProviderAvailable, onlineProviders0 } from "@/data/OnlineProviderHub.ts";
import { makeUsersAvailable } from "@/data/UserHub.ts";
import { toast } from "react-toastify";
import { clone } from "@atc-group/json-fn";

export const CANCEL_REASON = [
  "customerChangesOrCancelsOrder",
  "menuError",
  "kitchenDelays",
  "outOfStock",
  "paymentIssueOrRefundRequest",
  "technicalErrorInOrderSystem",
  "deliveryServiceUnavailability"
]
export const PLATFORM = {
  doordash: {
    name: "Doordash",
    icon: "/doordash-icon1@2x.png"
  },
  std: {
    name: "Skip The Dish",
    icon: "/iconskip-the-dishes1@2x.png"
  },
  uber_eats: {
    name: "Uber Eats",
    icon: "/iconuber-eats-icon1@2x.png"
  },
  deliverect: {
    name: "Deliverect",
    icon: "/deliverect.png"
  },
  pika: {
    name: "Pika",
    icon: "/pika-icon1@2x.png"
  },
  restablo: {
    name: "Restablo",
    icon: "/restablo.png"
  },
  lieferando: {
    name: "Lieferando",
    icon: "/iconskip-the-dishes1@2x.png"
  },
  phone: {
    name: "Phone",
    icon: "/iconholine@2x.png"
  },
  restaurant_plus: {
    name: "Restaurant Plus",
    icon: "/rplus-icon1@2x.png"
  },
  online_order: {
    name: "Online Order",
    icon: "delivery-online-order@2x.png"
  }
}
export type FilterDelivery = {
  searchCustomer?: { orderId: string, customer: any } // order._id
  searchId?: string // order.id
  creationDate?: number
  time?: number
  statuses?: string[]
  providers?: string[]
}

export const DeliveryStatusIcon = {
  [DeliveryStatus.READY_FOR_DELIVERY]: "/iconorder-already@2x.png",
  [DeliveryStatus.ON_THE_WAY]: "/iconorder-already@2x.png",
  [DeliveryStatus.CANCELLED]: "/iconclose@2x.png",
  [DeliveryStatus.ORDER_BEING_PREPARED]: "/iconcook@2x.png",
  [DeliveryStatus.DELIVERED]: "/icondone@2x.png",
  [DeliveryStatus.AWAITING_KITCHEN_APPROVAL]: "/iconloading@2x.png",
  [DeliveryStatus.UPCOMING]: "/iconlock@2x.png",
}

export const drivers = computed(() => {
  return users0().filter(u => u.role === UserRole.DRIVER)
})
export const [filterType, setFilterType] = signal<'allOrders' | 'preOrders'>('allOrders')
export const [filtersDelivery, setFiltersDelivery] = signal<FilterDelivery>({})
export const [onlineOrders1, setOnlineOrders1] = signal<Order[]>([])
export const [onlineOrdersCache, setOnlineOrdersCache] = signal<Order[]>([])
export const [onlineOrder0, setOnlineOrder0] = signal<Order>()
export const [isFirstRenderDeliveryM, setIsFirstRenderDeliveryM] = signal<boolean>(true)
export const [onlineOrdersPage, setOnlineOrdersPage] = signal<number>(4)
export const [isReachingEndOnlineOrders, setIsReachingEndOnlineOrders] = signal<boolean>(false)
export const [isValidatingOnlineOrders, setIsValidatingOnlineOrders] = signal<boolean>(true)
export const isCurrentOrderD = selector<OrderStrip, string | undefined>(onlineOrder0, (p1: any, p2: {
  _id: any
}) => p2?._id === p1)
export const [filterV, setFilterV] = signal(0)
export const [_v, _setV] = signal(0)
const ORDERS_PER_PAGE = 100

export function clearAllFilter() {
  setFiltersDelivery({})
  setFilterV(p => p + 1)
}

export async function onCompleteOrder(_order?: Order) {
  const order = _order || onlineOrder0()
  order.deliveryStatus = DeliveryStatus.DELIVERED
  delete order._meta
  delete order._deleted
  delete order._attachments
  delete order._rev
  console.log('[onCompleteOrder]', order)
  await OnlineOrder.upsert(order)
  toast.success('Complete success!')
}

window.filtersDelivery = filtersDelivery
window.clearAllFilter = clearAllFilter

function mappingStatus(orders: Order[]) {
  console.log('[mappingStatus] call order length:', orders.length)
  const now = dayjs()
  for (const order of orders) {
    if (order.deliveryStatus === DeliveryStatus.DELIVERED) continue
    const pickupDate = order.pickupDate
    if (pickupDate && dayjs(pickupDate).isAfter(now)) {
      order.isPreOrder = true
    }
    if (order.status === OrderStatus.IN_PROGRESS) {
      if (pickupDate && dayjs(pickupDate).isAfter(now)) {
        order.deliveryStatus = DeliveryStatus.UPCOMING
      } else {
        order.deliveryStatus = DeliveryStatus.AWAITING_KITCHEN_APPROVAL
      }
    } else if (order.status === OrderStatus.ACCEPTED) {
      order.deliveryStatus = DeliveryStatus.ORDER_BEING_PREPARED
      if (!_.isEmpty(order.driverInfo)) {
        order.deliveryStatus = DeliveryStatus.ON_THE_WAY
      }
    } else if (order.status === OrderStatus.READY) {
      order.deliveryStatus = DeliveryStatus.READY_FOR_DELIVERY
      if (!_.isEmpty(order.driverInfo)) {
        order.deliveryStatus = DeliveryStatus.ON_THE_WAY
      }
    } else if (order.status === OrderStatus.CANCELLED) {
      order.deliveryStatus = DeliveryStatus.CANCELLED
    }
  }
  return orders
}

export const assignDriverToOrder = async (driverId: string, order: Order = onlineOrder0()) => {
  const driver = users0()?.find(u => u._id === driverId)
  const driverInfo = _.clone(driver?.driverInfo)
  if (_.isEmpty(driverInfo)) {
    toast.warning('error when assign')
    return
  }
  const _order = order
  driverInfo._id = driverId
  _order.driverInfo = driverInfo
  if (_order.deliveryStatus !== DeliveryStatus.DELIVERED) delete _order.deliveryStatus
  delete _order._meta
  delete _order._deleted
  delete _order._attachments
  delete _order._rev
  console.log("[assignDriverToOrder] order is: ", _order)
  //todo: debug order
  await OnlineOrder.upsert(JSON.parse(JSON.stringify(clone(_order))))
}
//effect for filter order
effectOn([filterV, filtersDelivery, filterType], () => {
  const orders = mappingStatus(onlineOrdersCache())
  let ordersFilter = orders
  const filters = filtersDelivery() as FilterDelivery
  if (_.isEmpty(filters)) {
    const type = filterType()
    if (type === 'allOrders') {
      setOnlineOrders1(orders)
    } else {
      const preOrders = orders.filter(o => !!o.isPreOrder)
      setOnlineOrders1(preOrders)
    }
  } else {
    if (filters.searchId) {
      ordersFilter = ordersFilter.filter(o => o.id === filters.searchId)
    }
    if (filters.searchCustomer?.customer?.name) {
      ordersFilter = ordersFilter.filter(o => o.customerRaw?.name.includes(filters.searchCustomer?.customer?.name))
    }
    if (filters.creationDate) {
      ordersFilter = ordersFilter.filter(o =>
        dayjs(o.date).isSame(dayjs(filters.creationDate), 'day')
      )
    }
    if (filters.statuses?.length) {
      ordersFilter = ordersFilter.filter(o => filters.statuses!.includes(o.deliveryStatus || ''))
    }
    if (filters.providers?.length) {
      const providers = onlineProviders0()?.filter(o => filters.providers!.includes(o._id));
      ordersFilter = ordersFilter.filter(o =>
        providers.some(provider => provider.name === o.provider) || filters.providers!.includes(o.provider || '')
      );
    }
    if (filters.time) {
      ordersFilter = ordersFilter.filter(o => {
          return Math.abs(dayjs(o.date! * 1000).diff(dayjs(filters.time! * 1000), 'minute')) <= 1
        }
      );
    }
    setOnlineOrders1(ordersFilter)
  }
})

//effect fetch data
effectOn([onlineOrdersPage, _v], _.debounce(async () => {
  const query = {
    selector: {
      status: { $in: [...pendingOrderStatus, ...kitchenOrderStatus] }
    },
    limit: ORDERS_PER_PAGE,
    skip: ORDERS_PER_PAGE * (onlineOrdersPage() - 1),
    sort: [
      {
        date: 'desc',
      },
    ],
  }
  const onlineOrdersRaw = await OnlineOrder.find(query).exec()
  const onlineOrders = onlineOrdersRaw?.map(o => o.toMutableJSON())
  console.log('[fetchOnlineOrder] call -> order.length:', onlineOrders.length)
  setIsReachingEndOnlineOrders(onlineOrders.length === 0)
  if (onlineOrdersPage() === 1) {
    setOnlineOrdersCache([...onlineOrders])
    if (isFirstRenderDeliveryM()) {
      setOnlineOrder0(onlineOrders[0] as Order)
      setIsFirstRenderDeliveryM(false)
    }
  } else {
    setOnlineOrdersCache(prev => _.uniqBy([...prev, ...onlineOrders], '_id'))
  }
  setFilterV(p => p + 1)
  setIsValidatingOnlineOrders(false)
  console.log(`fetchOnlineOrders success, page ${onlineOrdersPage()} -> orders length: ${onlineOrders1()?.length}`)
}, 200), { defer: true })

//@ts-ignore
window.onlineOrders1 = onlineOrders1
//@ts-ignore
window.onlineOrder0 = onlineOrder0
